import React, {createContext, useState, useEffect} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import PropTypes from 'prop-types';
import MembershipService from '@tkww/orion-web-legacy-union--sdk-membership';
import Honeybadger from '@honeybadger-io/js';
import Cookies from 'js-cookie';
import getMagentoToken, {
  MAGENTO_CUSTOMER_TOKEN_KEY,
} from '@tkww/reg-shared-util-get-magento-token';
import useMagentoCustomer from '../../hooks/useMagentoCustomer';
import {obfuscate} from '../../utils/obfuscate';
import getMemberToken from '../../utils/getMemberToken';
import useIdentifyMember from './hooks/useIdentifyMember';
import noop from '../../utils/noop';

const membership = new MembershipService({
  clientOptions: {
    env: process.env.ENV,
    apiKey: process.env.UNION_MEMBERSHIP_API_KEY,
    version: 'v2',
    includeWeddingProperties: true,
  },
});

const mapWeddingDetailsToMember = memberData => ({
  ...memberData.member,
  number_of_guests: memberData?.wedding?.guest_range,
});

const Context = createContext({
  member: undefined,
  hasShippingAddress: false,
  fetchMember: noop,
  isFetchingMember: true,
  isLoadingAuth: false,
  logOut: noop,
  signUp: noop,
  logIn: noop,
  setIsLoadingAuth: noop,
});

export default Context;

export const MemberContextProvider = ({children}) => {
  const [isLoggedIn, setIsLoggedIn] = useState(Boolean(getMemberToken())); // optimistically set logged in status
  const [member, setMember] = useState(undefined);
  const [isFetchingMember, setIsFetchingMember] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(false);
  const [hasShippingAddress, setHasShippingAddress] = useState(false);

  useIdentifyMember(member, isLoggedIn);

  const client = useQueryClient();
  const {getMagentoCustomer, isFetchingCustomer} = useMagentoCustomer();

  const setCustomerToken = async (xoToken, id) => {
    await getMagentoToken();

    localStorage.setItem('member_id', id);
  };

  const setTheMember = async memberData => {
    const mappedMember = mapWeddingDetailsToMember(memberData);

    Cookies.remove('cart_id', {domain: '.theknot.com'});

    await setCustomerToken(memberData.session.token, mappedMember.id);

    setMember(mappedMember);
    setIsLoadingAuth(false);
    setIsLoggedIn(true);
  };

  /**
   * Checks and denotes if the magento customer has a shipping address
   * optimistically (using an IIFE prevents blocking the main thread).
   *
   * @return  void
   */
  const checkShippingAddressStatusOptimistically = async () => {
    (async () => {
      const {data: {customer} = {}} = await getMagentoCustomer();

      setHasShippingAddress(customer?.addresses?.length > 0);
    })();
  };

  useEffect(() => {
    const sessionToken = getMemberToken();

    if (!sessionToken) return;

    const initMember = async () => {
      setIsFetchingMember(true);

      try {
        const {data: getMemberResponse} = await membership.getMember({
          token: sessionToken,
        });

        if (getMemberResponse.member) {
          const mappedMember = mapWeddingDetailsToMember(getMemberResponse);

          await setCustomerToken(sessionToken, mappedMember.id);

          checkShippingAddressStatusOptimistically();

          setMember(mappedMember);

          Honeybadger.setContext({
            memberId: mappedMember.id,
            membershipSessionToken: obfuscate(sessionToken),
            user_id: mappedMember.id,
          });

          Honeybadger.beforeNotify(notice => {
            const sessionUrl = window.FS.getCurrentSessionURL(true);

            if (sessionUrl) {
              // eslint-disable-next-line no-param-reassign
              notice.context.url = sessionUrl;
            }
          });
        } else {
          setIsLoggedIn(false);
        }
      } catch (err) {
        setIsLoggedIn(false);

        Honeybadger.notify(err, 'MagentoCustomerTokenFetchError');
      }

      setIsFetchingMember(false);
    };

    initMember();
  }, []); // eslint-disable-line

  const logOut = async () => {
    client.clear();

    await membership.logOut();

    localStorage.removeItem('member_id');
    localStorage.removeItem(MAGENTO_CUSTOMER_TOKEN_KEY);

    Cookies.remove('cart_id', {domain: '.theknot.com'});

    setHasShippingAddress(false);
    setMember(null);
    setIsLoggedIn(false);

    Honeybadger.clear();
  };

  const signUp = async options => {
    setIsLoadingAuth(true);
    setHasShippingAddress(false);

    client.clear();

    const {data: signedInMember} = await membership.signUp(options);

    const theMember = await setTheMember(signedInMember);

    return theMember;
  };

  const logIn = async options => {
    setIsLoadingAuth(true);
    setHasShippingAddress(false);

    const {data: loggedInMember} = await membership.logIn(options);

    const theMember = await setTheMember(loggedInMember);

    checkShippingAddressStatusOptimistically();

    return theMember;
  };

  return (
    <Context.Provider
      value={{
        isLoggedIn,
        member,
        logOut,
        isFetchingMember,
        signUp,
        logIn,
        hasShippingAddress,
        isFetchingCustomer,
        isLoadingAuth,
        setIsLoadingAuth,
      }}>
      {children}
    </Context.Provider>
  );
};

MemberContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
